// The side dialog's lock toggles, as derivation rather than as component state.
//
// "Locked" is the operator's word for a resource that still works the day but takes no new orders.
// It is the inverse of `accepting`, which is what the data calls it — every boundary in here flips
// once, on purpose, so the UI can read in the operator's direction and the writes in the schema's.
//
// Pending edits live in one `{ [resource_id]: accepting }` map rather than on each card, so Lock
// All is the same operation as one toggle applied N times, and Save can ask a single question.

import { NO_WRITE, planSchedulingWrite, collectSchedulingWrites } from '@/utilities/schedulingOverride';

export const resourceKey = (entry) => entry?.resource?.resource_id;

// Saved state, ignoring anything staged. `accepting` defaults true: a resource with no closure on
// the day is open, and a missing dayState means the store never resolved one.
export const savedAccepting = (entry) => entry?.dayState?.accepting ?? entry?.accepting ?? true;

export const effectiveAccepting = (entry, pending = {}) => {
    const staged = pending[resourceKey(entry)];

    return staged == null ? savedAccepting(entry) : staged;
};

export const isLocked = (entry, pending) => !effectiveAccepting(entry, pending);

// Closing a day no rule covers strands the resource: the closure is its only row on the day, so
// hiding it takes the card — and the toggle that would bring it back — off the calendar with it.
// planSchedulingWrite refuses this too; here it is a disabled toggle rather than a silent no-op.
export const canLock = (entry) => !!entry?.dayState?.coveredByRule;

// Locking is the direction that can be refused, so a resource already locked can always unlock.
export const toggleDisabled = (entry, pending) => !isLocked(entry, pending) && !canLock(entry);

// "This day", not "today": the dialog opens on any day, and a one-off can be booked for next week.
export const LOCK_HINT = 'Only runs this day — delete it instead of locking it';

// One button for the whole day: lock everything it can, unless there is nothing left to lock, in
// which case it offers the way back. Resources that cannot be locked are ignored by both
// directions rather than making the button read as a partial success.
export const lockAllLocks = (entries = [], pending = {}) =>
    entries.some((entry) => canLock(entry) && !isLocked(entry, pending));

export const lockAllLabel = (entries, pending) => (lockAllLocks(entries, pending) ? 'Lock All' : 'Unlock All');

// The map Lock All stages. Locking is the direction that can be refused, so a resource that cannot
// be locked keeps whatever it had; unlocking is never refused and applies to all of them.
export const lockAllPending = (entries = [], pending = {}) => {
    const accepting = !lockAllLocks(entries, pending);

    return entries.reduce((rv, entry) => {
        const applies = accepting || canLock(entry);

        return { ...rv, [resourceKey(entry)]: applies ? accepting : effectiveAccepting(entry, pending) };
    }, {});
};

export const changedEntries = (entries = [], pending = {}) =>
    entries.filter((entry) => effectiveAccepting(entry, pending) !== savedAccepting(entry));

export const hasPendingChange = (entries, pending) => changedEntries(entries, pending).length > 0;

// Planned against each entry's own dayState — the one the store resolved for this resource-day, so
// a closure being dropped names the row that actually covers it.
export const schedulingWrites = ({ entries = [], pending = {}, day } = {}) =>
    collectSchedulingWrites(
        changedEntries(entries, pending).map((entry) =>
            day
                ? planSchedulingWrite({
                      resource: entry.resource,
                      day,
                      dayState: entry.dayState,
                      accepting: effectiveAccepting(entry, pending),
                  })
                : NO_WRITE
        )
    );

export const hasWrites = ({ overrides = [], deletes = [] } = {}) => overrides.length > 0 || deletes.length > 0;
